import React from 'react';
import StateDropdown from '../StateDropdown';

// Room templates with predefined elements
const ROOM_TEMPLATES = {
  salon: {
    name: 'Salon / Pièce à vivre',
    elements: [
      'Revêtements des sols',
      'Murs / menuiseries',
      'Plafond',
      'Electricité / Plomberie'
    ]
  },
  chambre: {
    name: 'Chambre',
    elements: [
      'Revêtements des sols',
      'Murs / menuiseries',
      'Plafond',
      'Electricité / Plomberie'
    ]
  },
  wc: {
    name: 'WC',
    elements: [
      'Revêtements des sols',
      'Murs / menuiseries',
      'Plafond',
      'Electricité / Plomberie',
      'Sanitaires'
    ]
  },
  sdb: {
    name: 'Salle de bain',
    elements: [
      'Menuiseries',
      'Plafond',
      'Revêtements des sols',
      'Murs',
      'Rangements',
      'Baignoire / Douche',
      'Éviers, robinetterie',
      'Chauffage, tuyauterie'
    ]
  },
  cuisine: {
    name: 'Cuisine',
    elements: [
      'Revêtements des sols',
      'Murs / menuiseries',
      'Plafond',
      'Electricité / Plomberie',
      'Meubles de cuisine',
      'Evier',
      'Hotte',
      'Plaque de cuisson',
      'Réfrigérateur',
      'Lave-vaiselle'
    ]
  }
};

const RoomInspectionSection = ({ rooms, onChange, etatType }) => {
  const isEntree = etatType === 'entrée';

  // Add a new room
  const addRoom = (templateKey) => {
    const template = ROOM_TEMPLATES[templateKey];
    const newRoom = {
      roomType: templateKey,
      roomName: template.name,
      elements: template.elements.map(elementName => ({
        name: elementName,
        entryState: '',
        exitState: '',
        comments: ''
      }))
    };

    onChange([...rooms, newRoom]);
  };

  // Remove a room
  const removeRoom = (index) => {
    const updatedRooms = rooms.filter((_, i) => i !== index);
    onChange(updatedRooms);
  };

  // Update a specific element in a room
  const updateRoomElement = (roomIndex, elementIndex, field, value) => {
    const updatedRooms = [...rooms];
    updatedRooms[roomIndex].elements[elementIndex][field] = value;
    onChange(updatedRooms);
  };

  // Update room name (for custom rooms)
  const updateRoomName = (roomIndex, value) => {
    const updatedRooms = [...rooms];
    updatedRooms[roomIndex].roomName = value;
    onChange(updatedRooms);
  };

  return (
    <div className="space-y-6">
      {/* Existing rooms */}
      {rooms.map((room, roomIndex) => (
        <div key={roomIndex} className="border border-gray-300 rounded-lg p-4 bg-white">
          {/* Room header */}
          <div className="flex justify-between items-center mb-4">
            <div className="flex-1">
              {room.roomType === 'autre' ? (
                <input
                  type="text"
                  value={room.roomName}
                  onChange={(e) => updateRoomName(roomIndex, e.target.value)}
                  placeholder="Nom de la pièce"
                  className="text-lg font-semibold border-b border-gray-300 focus:border-blue-500 focus:outline-none"
                />
              ) : (
                <h3 className="text-lg font-semibold text-gray-800">{room.roomName}</h3>
              )}
            </div>
            <button
              type="button"
              onClick={() => removeRoom(roomIndex)}
              className="text-red-600 hover:text-red-800 font-medium"
            >
              Supprimer
            </button>
          </div>

          {/* Elements table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Éléments
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    État d'entrée
                  </th>
                  {!isEntree && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      État de sortie
                    </th>
                  )}
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Commentaires
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {room.elements.map((element, elementIndex) => (
                  <tr key={elementIndex}>
                    <td className="px-3 py-2 text-sm text-gray-900">
                      {element.name}
                    </td>
                    <td className="px-3 py-2">
                      <StateDropdown
                        value={element.entryState}
                        onChange={(e) => updateRoomElement(roomIndex, elementIndex, 'entryState', e.target.value)}
                        name={`rooms[${roomIndex}].elements[${elementIndex}].entryState`}
                        disabled={false}
                      />
                    </td>
                    {!isEntree && (
                      <td className="px-3 py-2">
                        <StateDropdown
                          value={element.exitState}
                          onChange={(e) => updateRoomElement(roomIndex, elementIndex, 'exitState', e.target.value)}
                          name={`rooms[${roomIndex}].elements[${elementIndex}].exitState`}
                          disabled={false}
                        />
                      </td>
                    )}
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={element.comments}
                        onChange={(e) => updateRoomElement(roomIndex, elementIndex, 'comments', e.target.value)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        placeholder="Commentaires..."
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* Add room buttons */}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Ajouter une pièce</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <button
            type="button"
            onClick={() => addRoom('salon')}
            className="px-4 py-2 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors"
          >
            + Salon
          </button>
          <button
            type="button"
            onClick={() => addRoom('chambre')}
            className="px-4 py-2 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors"
          >
            + Chambre
          </button>
          <button
            type="button"
            onClick={() => addRoom('sdb')}
            className="px-4 py-2 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors"
          >
            + Salle de bain
          </button>
          <button
            type="button"
            onClick={() => addRoom('wc')}
            className="px-4 py-2 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors"
          >
            + WC
          </button>
          <button
            type="button"
            onClick={() => addRoom('cuisine')}
            className="px-4 py-2 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors"
          >
            + Cuisine
          </button>
          <button
            type="button"
            onClick={() => addRoom('autre')}
            className="px-4 py-2 bg-gray-50 text-gray-700 rounded-md hover:bg-gray-100 transition-colors"
          >
            + Autre pièce
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoomInspectionSection;
